import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { brmAPI } from "@/services/api";
import { ArrowLeft, Users, Gift, TrendingUp, Copy, CheckCircle2 } from "lucide-react";
import {
  TailAdminEmptyState,
  TailAdminMetricCard,
  TailAdminPanel,
} from "@/components/template/tailadmin/DashboardPrimitives";

const BRMReferrals = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, referralsRes] = await Promise.all([
        brmAPI.getProfile(),
        brmAPI.getReferrals()
      ]);
      setProfile(profileRes.data);
      setReferrals(referralsRes.data);
    } catch (error) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const copyReferralCode = () => {
    if (profile?.referral_code) {
      navigator.clipboard.writeText(profile.referral_code);
      setCopied(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyReferralLink = () => {
    if (profile?.referral_code) {
      const link = `${window.location.origin}/brm/login?ref=${profile.referral_code}`;
      navigator.clipboard.writeText(link);
      toast.success("Referral link copied!");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const totalBonus = referrals.reduce((sum, r) => sum + (r.bonus_earned || 0), 0);

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/brm/dashboard")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold tracking-tight text-gray-900">My Referrals</h1>
          <p className="text-sm text-gray-500">Invite agents and earn bonuses</p>
        </div>
      </div>

      {/* Referral Code Card */}
      <section className="rounded-2xl border border-brand-200 bg-brand-500 p-6 text-white shadow-theme-xs">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-white/80 text-sm">Your Referral Code</p>
              {loading ? (
                <Skeleton className="h-8 w-32 bg-white/20" />
              ) : (
                <p className="text-2xl font-black tracking-wider" data-testid="referral-code">
                  {profile?.referral_code || "N/A"}
                </p>
              )}
            </div>
            <div className="p-3 rounded-full bg-white/20">
              <Gift className="w-6 h-6" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={copyReferralCode}
              className="flex-1"
              data-testid="copy-code-btn"
            >
              {copied ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              Copy Code
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={copyReferralLink}
              className="flex-1 bg-transparent border-white text-white hover:bg-white hover:text-brand-500"
              data-testid="copy-link-btn"
            >
              Copy Link
            </Button>
          </div>
      </section>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <TailAdminMetricCard label="Total Referrals" value={referrals.length} icon={Users} helper="Agents invited" loading={loading} />
        <TailAdminMetricCard label="Total Earned" value={formatCurrency(totalBonus)} icon={TrendingUp} tone="success" helper="Referral bonus" loading={loading} />
      </div>

      {/* How it works */}
      <TailAdminPanel title="How Referral Bonuses Work" action={null}>
          <ul className="text-sm text-gray-500 space-y-2">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-brand-50 text-brand-500 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
              <span>Share your referral code with other agents</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-brand-50 text-brand-500 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
              <span>They sign up using your code</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-brand-50 text-brand-500 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
              <span>You earn <strong className="text-brand-500">5%</strong> of their commissions automatically</span>
            </li>
          </ul>
      </TailAdminPanel>

      {/* Referrals List */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
          Referred Agents ({referrals.length})
        </h2>
        
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <section key={i} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs">
                <div className="flex items-center gap-3">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-32 mt-1" />
                  </div>
                  <Skeleton className="h-5 w-16" />
                </div>
            </section>
          ))
        ) : referrals.length === 0 ? (
          <TailAdminEmptyState
            icon={Users}
            title="No referrals yet"
            description="Share your code to start earning bonuses."
          />
        ) : (
          referrals.map((referral) => (
            <section key={referral.id} data-testid={`referral-${referral.id}`} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center">
                    <Users className="w-5 h-5 text-brand-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{referral.name}</p>
                    <p className="text-xs text-gray-500">
                      {referral.phone} - Joined {formatDate(referral.joined_at)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {referral.active_enrollments} active enrollments
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-600 text-sm">
                      {formatCurrency(referral.bonus_earned)}
                    </p>
                    <p className="text-xs text-gray-500">earned</p>
                  </div>
                </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
};

export default BRMReferrals;
